<div class="container">

  <header class="page-heading">
    <h1>FIRE Calculator</h1>
    <p>Find your FIRE number and the age you can retire early, with year-wise projections and a shortfall action plan.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">FIRE Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('currentAge')">
        <label class="field-label" for="currentAge">Current Age</label>
        <div class="input-shell suffix">
          <input id="currentAge" type="number"
            [(ngModel)]="currentAge"
            [min]="C.currentAge.min" [max]="C.currentAge.max" [step]="C.currentAge.step"
            (ngModelChange)="onFieldChange('currentAge')"
            (blur)="onFieldBlur('currentAge')"
            placeholder="e.g. 30"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('currentAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentAge') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('retirementAge')">
        <label class="field-label" for="retirementAge">Target Retirement (FIRE) Age</label>
        <div class="input-shell suffix">
          <input id="retirementAge" type="number"
            [(ngModel)]="retirementAge"
            [min]="C.retirementAge.min" [max]="C.retirementAge.max" [step]="C.retirementAge.step"
            (ngModelChange)="onFieldChange('retirementAge')"
            (blur)="onFieldBlur('retirementAge')"
            placeholder="e.g. 50"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        @if (hasError('retirementAge')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('retirementAge') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('lifeExpectancy')">
        <label class="field-label" for="lifeExpectancy">Life Expectancy</label>
        <div class="input-shell suffix">
          <input id="lifeExpectancy" type="number"
            [(ngModel)]="lifeExpectancy"
            [min]="C.lifeExpectancy.min" [max]="C.lifeExpectancy.max" [step]="C.lifeExpectancy.step"
            (ngModelChange)="onFieldChange('lifeExpectancy')"
            (blur)="onFieldBlur('lifeExpectancy')"
            placeholder="e.g. 85"
            autocomplete="off"/>
          <span class="affix right">Yr</span>
        </div>
        <div class="field-hint">The age your retirement corpus should last until</div>
        @if (hasError('lifeExpectancy')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('lifeExpectancy') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('currentMonthlyExpense')">
        <label class="field-label" for="currentMonthlyExpense">Current Monthly Expenses</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="currentMonthlyExpense" type="number"
            [(ngModel)]="currentMonthlyExpense"
            [min]="C.monthlyExpenses.min" [max]="C.monthlyExpenses.max" [step]="C.monthlyExpenses.step"
            (ngModelChange)="onFieldChange('currentMonthlyExpense')"
            (blur)="onFieldBlur('currentMonthlyExpense')"
            placeholder="e.g. 50,000"
            autocomplete="off"/>
        </div>
        @if (hasError('currentMonthlyExpense')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentMonthlyExpense') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('currentSavings')">
        <label class="field-label" for="currentSavings">Current Savings &amp; Investments</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="currentSavings" type="number"
            [(ngModel)]="currentSavings"
            [min]="C.currentSavings.min" [max]="C.currentSavings.max" [step]="C.currentSavings.step"
            (ngModelChange)="onFieldChange('currentSavings')"
            (blur)="onFieldBlur('currentSavings')"
            placeholder="e.g. 10,00,000"
            autocomplete="off"/>
        </div>
        @if (hasError('currentSavings')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('currentSavings') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('monthlySip')">
        <label class="field-label" for="monthlySip">Monthly SIP Investment</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlySip" type="number"
            [(ngModel)]="monthlySip"
            [min]="C.monthlySip.min" [max]="C.monthlySip.max" [step]="C.monthlySip.step"
            (ngModelChange)="onFieldChange('monthlySip')"
            (blur)="onFieldBlur('monthlySip')"
            placeholder="e.g. 30,000"
            autocomplete="off"/>
        </div>
        @if (hasError('monthlySip')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('monthlySip') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('preReturnRate')">
        <label class="field-label" for="preReturnRate">Expected Return (Pre-Retirement)</label>
        <div class="input-shell suffix">
          <input id="preReturnRate" type="number"
            [(ngModel)]="preReturnRate"
            [min]="C.preReturn.min" [max]="C.preReturn.max" [step]="C.preReturn.step"
            (ngModelChange)="onFieldChange('preReturnRate')"
            (blur)="onFieldBlur('preReturnRate')"
            placeholder="e.g. 12"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('preReturnRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('preReturnRate') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('postReturnRate')">
        <label class="field-label" for="postReturnRate">Expected Return (Post-Retirement)</label>
        <div class="input-shell suffix">
          <input id="postReturnRate" type="number"
            [(ngModel)]="postReturnRate"
            [min]="C.postReturn.min" [max]="C.postReturn.max" [step]="C.postReturn.step"
            (ngModelChange)="onFieldChange('postReturnRate')"
            (blur)="onFieldBlur('postReturnRate')"
            placeholder="e.g. 8"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Usually lower than pre-retirement, for a safer asset mix</div>
        @if (hasError('postReturnRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('postReturnRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('inflationRate')">
        <label class="field-label" for="inflationRate">Expected Inflation Rate</label>
        <div class="input-shell suffix">
          <input id="inflationRate" type="number"
            [(ngModel)]="inflationRate"
            [min]="C.inflation.min" [max]="C.inflation.max" [step]="C.inflation.step"
            (ngModelChange)="onFieldChange('inflationRate')"
            (blur)="onFieldBlur('inflationRate')"
            placeholder="e.g. 6"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('inflationRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('inflationRate') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('swr')">
        <label class="field-label" for="swr">Safe Withdrawal Rate</label>
        <div class="input-shell suffix">
          <input id="swr" type="number"
            [(ngModel)]="swr"
            [min]="C.swr.min" [max]="C.swr.max" [step]="C.swr.step"
            (ngModelChange)="onFieldChange('swr')"
            (blur)="onFieldBlur('swr')"
            placeholder="e.g. 4"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Typically 3–4% per year (the "4% rule") sets your FIRE number</div>
        @if (hasError('swr')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('swr') }}
          </div>
        }
      </div>
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="savings-banner mb-md" [style.background]="result.surplus >= 0 ? 'linear-gradient(90deg, #059669 0%, #10b981 100%)' : 'linear-gradient(90deg, #dc2626 0%, #ef4444 100%)'">
      <div class="sb-item">
        <div class="sb-val">
          @if (result.surplus >= 0) { Surplus of {{ fmt.currency(result.surplus) }} } @else { Shortfall of {{ fmt.currency(-result.surplus) }} }
        </div>
        <div class="sb-lbl">projected at your target retirement age of {{ retirementAge }}</div>
      </div>
      <div class="sb-divider"></div>
      <div class="sb-item">
        <div class="sb-val">
          @if (result.depletionAge) { Corpus runs out at age {{ Math.floor(result.depletionAge) }} } @else { Lasts to age {{ lifeExpectancy }}, {{ fmt.currency(result.corpusAtLifeExpectancy) }} left }
        </div>
        <div class="sb-lbl">based on your post-retirement return</div>
      </div>
    </div>

    @if (result.surplus < 0) {
      <div class="card mb-md">
        <div class="section-title">Close the Gap</div>
        <p>
          To fully fund your FIRE number by age {{ retirementAge }}, increase your monthly SIP from
          {{ fmt.currency(monthlySip) }} to about <strong>{{ fmt.currency(result.requiredMonthlySip) }}</strong>,
          assuming the same {{ preReturnRate }}% pre-retirement return.
        </p>
      </div>
    }

    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">FIRE Age</div>
        <div class="sc-value">
          @if (result.fireAge !== null) { {{ Math.floor(result.fireAge) }} } @else { 100+ }
        </div>
        <div class="sc-sub">
          @if (result.yearsToFire !== null) { {{ Math.floor(result.yearsToFire) }} years from now } @else { beyond your horizon }
        </div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Corpus at Retirement</div>
        <div class="sc-value">{{ fmt.currency(result.corpusAtRetirement) }}</div>
        <div class="sc-sub">at age {{ retirementAge }}</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Required (FI Number)</div>
        <div class="sc-value">{{ fmt.currency(result.requiredCorpusAtRetirement) }}</div>
        <div class="sc-sub">@ {{ swr }}% withdrawal rate</div>
      </div>
      <div class="summary-card" [class.c-green]="result.surplus >= 0" [class.c-red]="result.surplus < 0">
        <div class="sc-icon"></div>
        <div class="sc-label">{{ result.surplus >= 0 ? 'Surplus' : 'Shortfall' }}</div>
        <div class="sc-value">{{ fmt.currency(Math.abs(result.surplus)) }}</div>
        <div class="sc-sub">vs your FI number</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Invested vs Gain (at Retirement)</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Your Corpus vs FI Target</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-by-Year Projection</div>

      <div class="tab-bar">
        <span class="tab-btn active">Yearly Summary</span>
        <div class="tab-count">{{ result.years.length }} years</div>
      </div>

      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Age</th>
              <th class="t-left">Phase</th>
              <th>Annual Expense</th>
              <th>FI Target Corpus</th>
              <th class="t-balance">Your Corpus</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.years; track row.year) {
              <tr>
                <td class="t-left t-year">Age {{ row.age }}</td>
                <td class="t-left">{{ row.phase === 'accumulating' ? 'Accumulating' : 'Retired' }}</td>
                <td>{{ fmt.currency(row.annualExpense) }}</td>
                <td>{{ fmt.currency(row.requiredCorpus) }}</td>
                <td class="t-balance">{{ fmt.currency(row.investedCorpus) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is FIRE (Financial Independence, Retire Early) Calculated?</h2>
    <p>
      FIRE is the point where your investments can sustainably cover your living expenses without
      needing active income. This calculator projects your invested corpus — growing from your
      current savings and monthly SIP — and compares it, year by year, against your "FI number":
      the corpus required to safely withdraw your inflation-adjusted expenses each year.
    </p>
    <p class="emi-formula">FI Number = (Annual Expenses &times; (1 + Inflation)<sup>years</sup>) / Safe Withdrawal Rate</p>
    <ul class="emi-formula-vars">
      <li><strong>Annual Expenses</strong> &mdash; Your current yearly expenses, before inflation</li>
      <li><strong>Safe Withdrawal Rate</strong> &mdash; The share of your corpus you can withdraw each year, commonly 4%</li>
    </ul>
    <p>
      Your <strong>FIRE age</strong> is the first point at which your projected corpus, growing with
      your monthly SIP, crosses this inflation-adjusted FI number — independent of the retirement
      age you actually choose. The <strong>retirement outlook</strong> then checks whether the corpus
      you'll actually have at your chosen retirement age is enough, and whether it lasts through your
      life expectancy once you start drawing it down.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>What is the "4% rule"?</h3>
      <p>
        The 4% rule is a rule of thumb suggesting you can withdraw 4% of your retirement corpus in
        the first year, and adjust that amount for inflation every year after, with a low risk of
        running out of money over a typical retirement horizon. A lower withdrawal rate (e.g. 3%) is
        more conservative and needs a larger corpus.
      </p>
    </div>
    <div class="faq-item">
      <h3>Why are pre- and post-retirement returns different?</h3>
      <p>
        Most people shift toward a more conservative, less equity-heavy portfolio after retiring
        since they can no longer rely on a salary to ride out market downturns. This calculator lets
        you model a higher growth-focused return before retirement and a more conservative return
        after.
      </p>
    </div>
    <div class="faq-item">
      <h3>What if my projected corpus falls short?</h3>
      <p>
        The calculator shows the higher monthly SIP needed to close the gap by your target retirement
        age. You can also push your retirement age out, trim expenses, or aim for a higher expected
        return to close the same gap.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/what-is-fire-how-much-do-you-need">What is FIRE and how much do you need in India?</a>
    </p>
  </div>
</section>
